import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { useConfirmationModalStore, usePreloaderStore } from '../../../../../../stores';
import { QuestionType, Endpoints } from '../../../../../../views/democracy/constants';
import { valueToMediaRefs, buildMultipartRequest, extractErrorMessage } from '../../../../../../views/democracy/utils';
import AppMediaList from '../../../controls/media/AppMediaList';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';

// Модалка редактирования вопроса (kahoot-варианты + медиа-список).
export default function QuestionEditModal({ question, onChanged, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const { open: openConfirmation } = useConfirmationModalStore();
    const { show, hide } = usePreloaderStore();

    const [data, setData] = useState({
        orderId: question.orderId,
        questionText: question.questionText || '',
        answerText: question.answerText || '',
        cost: question.cost ?? 10,
        type: question.type || QuestionType.SIMPLE,
        kahoot: question.kahoot || null,
        media: Array.isArray(question.media) ? question.media : []
    });
    const [pendingFiles, setPendingFiles] = useState([]);

    const isKahoot = data.type === QuestionType.KAHOOT;
    const setField = (field, value) => setData(prev => ({ ...prev, [field]: value }));

    const toggleKahoot = () => {
        setData(prev => prev.type === QuestionType.KAHOOT
            ? { ...prev, type: QuestionType.SIMPLE, kahoot: null }
            : { ...prev, type: QuestionType.KAHOOT, kahoot: prev.kahoot || { option1: '', option2: '', option3: '' } });
    };

    const setOption = (key, value) => setData(prev => ({ ...prev, kahoot: { ...prev.kahoot, [key]: value } }));

    const handleSave = async () => {
        if (isKahoot && data.kahoot) {
            const opts = [data.kahoot.option1, data.kahoot.option2, data.kahoot.option3];
            if (!opts.includes(data.answerText)) {
                enqueueSnackbar(`Ответ "${data.answerText}" не совпадает ни с одним вариантом`, { variant: 'error' });
                return;
            }
        }
        show();
        try {
            const payload = {
                orderId: parseInt(data.orderId, 10),
                type: data.type,
                cost: parseInt(data.cost, 10) || 0,
                questionText: data.questionText,
                answerText: data.answerText,
                kahoot: isKahoot ? data.kahoot : null,
                media: valueToMediaRefs(data.media)
            };
            const formData = buildMultipartRequest(payload, pendingFiles);
            await apiClient.put(Endpoints.questions.update(question.id), formData);
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onChanged();
            onClose();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить вопрос'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleDelete = () => {
        openConfirmation(`Удалить вопрос #${question.orderId}?`, async () => {
            show();
            try {
                await apiClient.delete(Endpoints.questions.delete(question.id));
                enqueueSnackbar('Удалено', { variant: 'success' });
                onChanged();
                onClose();
            } catch (error) {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось удалить'), { variant: 'error' });
            } finally {
                hide();
            }
        });
    };

    const footer = (
        <>
            <button type="button" className="btn btn-danger" onClick={handleDelete}>Удалить</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
        </>
    );

    return (
        <Modal open title="Редактирование вопроса" onClose={onClose} size="lg" footer={footer}>
            <div className="set-row2">
                <LabeledInput label="Порядковый номер" value={String(data.orderId)} onChange={(e) => setField('orderId', e.target.value)} />
                <LabeledInput label="Стоимость" value={String(data.cost)} onChange={(e) => setField('cost', e.target.value)} />
            </div>

            <div>
                <label className="ctl-label">Вопрос</label>
                <textarea className="textarea" value={data.questionText} onChange={(e) => setField('questionText', e.target.value)} />
            </div>

            <div className={`tg-row${isKahoot ? ' on' : ''}`} onClick={toggleKahoot}>
                <span className="lab">Выбор из нескольких вариантов ответов</span>
                <span className="sw" />
            </div>

            {isKahoot && data.kahoot && (
                <div className="set-row2" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
                    <LabeledInput label="1 вариант" value={data.kahoot.option1 || ''} onChange={(e) => setOption('option1', e.target.value)} />
                    <LabeledInput label="2 вариант" value={data.kahoot.option2 || ''} onChange={(e) => setOption('option2', e.target.value)} />
                    <LabeledInput label="3 вариант" value={data.kahoot.option3 || ''} onChange={(e) => setOption('option3', e.target.value)} />
                </div>
            )}

            <div>
                <label className="ctl-label">Ответ</label>
                <textarea className="textarea" value={data.answerText} onChange={(e) => setField('answerText', e.target.value)} />
            </div>

            <div>
                <label className="ctl-label">Медиа</label>
                <AppMediaList
                    value={data.media}
                    pendingFiles={pendingFiles}
                    onChange={(nextValue, nextPending) => { setField('media', nextValue); setPendingFiles(nextPending); }}
                />
            </div>
        </Modal>
    );
}

QuestionEditModal.propTypes = {
    question: PropTypes.shape({
        id: PropTypes.number.isRequired,
        orderId: PropTypes.number,
        questionText: PropTypes.string,
        answerText: PropTypes.string,
        cost: PropTypes.number,
        type: PropTypes.string,
        kahoot: PropTypes.object,
        media: PropTypes.array
    }).isRequired,
    onChanged: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
